import Axios from 'axios';
import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import Button from '~/components/button';
import ContainerFlex from '~/components/containerFlex';
import Dialog from '~/components/dialog';
import LocalDropzone from '~/components/dropZone';
import Loading from '~/components/loading';
import Pagination from '~/components/pagination';
import Select from '~/components/select';
import useApi, { ApiMethod } from '~/hooks/useApi';
import { useModal } from '~/hooks/useModal';
import Endpoints from '~/services/endpoints';
import Constants from '~/utils/constants';
import { cpfMask, phoneMask } from '~/utils/masks';
import * as St from '../styles';
import { downloadZeussDemoXlsx } from '../utils';

interface IUploadResponse {
  mensagem?: string;
}

interface IZeussClient {
  id?: number | string;
  idCliente?: number | string;
  nome?: string;
  cpf?: string;
  celular?: string;
  telefone?: string;
  email?: string;
  limite?: number;
  dataImportacao?: string;
  [key: string]: unknown;
  status?: string;
}

interface IFeedbackState {
  open: boolean;
  message: string;
}

interface IDeleteDialogState {
  open: boolean;
  message: string;
  clientId: number | string | null;
}

interface IZeussClientsResponse {
  data: IZeussClient[];
  page: number;
  pageSize: number;
  totalPages: number;
  totalSize: number;
}

const ZeussFileUpload: React.FC = () => {
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [importedFileName, setImportedFileName] = useState<string | null>(null);

  const [clients, setClients] = useState<IZeussClient[]>([]);
  const [deleteClientId, setDeleteClientId] = useState<number | string | null>(
    null,
  );
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [feedback, setFeedback] = useState<IFeedbackState>({
    open: false,
    message: '',
  });
  const [dialogDelete, setDialogDelete] = useState<IDeleteDialogState>({
    open: false,
    message: 'Deseja excluir o cliente?',
    clientId: null,
  });

  const apiGetActiveClients = useApi<IZeussClientsResponse>({
    url: Endpoints.files.getZeussClients,
    method: ApiMethod.GET,
    tryNewWay: true,
    fetchOnMount: false,
  });

  const apiExportClients = useApi<IZeussClientsResponse>({
    url: Endpoints.files.getZeussClients,
    method: ApiMethod.GET,
    tryNewWay: true,
    fetchOnMount: false,
  });

  const apiDeleteClient = useApi<IUploadResponse>({
    url: Endpoints.files.deleteZeussClient.replace(
      'PARAM_ID_CLIENTE',
      `${deleteClientId || ''}`,
    ),
    method: ApiMethod.DELETE,
    fetchOnMount: false,
  });

  const showFeedback = (message: string) => {
    setFeedback({ open: true, message });
  };

  const getClientsFromResponse = (responseData: unknown): IZeussClient[] => {
    if (Array.isArray(responseData)) return responseData;

    if (
      responseData &&
      typeof responseData === 'object' &&
      Array.isArray((responseData as { data?: IZeussClient[] }).data)
    ) {
      return (responseData as { data: IZeussClient[] }).data;
    }

    return [];
  };

  const [form, setForm] = useState({ status: 'ATIVO' });
  const getStatusFilter = () => (form.status === 'TODOS' ? null : form.status);

  const fetchClients = async (page = 1, size = 10) => {
    const payload = {
      page,
      size,
      status: getStatusFilter(),
    };
    const response = await apiGetActiveClients.callApi(payload);

    if (!response.success) {
      showFeedback(response.mensagem || 'Erro ao buscar clientes Zeuss.');
      setClients([]);
      return;
    }
    setClients(getClientsFromResponse(response.data));
  };

  const escapeCsvValue = (value: unknown) => {
    if (value === null || value === undefined) return '';

    return `"${String(value).replace(/"/g, '""')}"`;
  };

  const buildZeussCsv = (clientsToExport: IZeussClient[]) => {
    const headers = [
      'Nome',
      'CPF',
      'Telefone',
      'E-mail',
      'Limite',
      'Data de Importação',
      'Status',
    ];

    const rows = clientsToExport.map((client) => [
      client.nome || '',
      client.cpf || '',
      (client.telefone as string) || (client.celular as string) || '',
      client.email || '',
      client.limite ?? '',
      client.dataImportacao
        ? moment(client.dataImportacao).format('DD/MM/YYYY')
        : '',
      client.status || '',
    ]);

    return [headers, ...rows]
      .map((row) => row.map(escapeCsvValue).join(','))
      .join('\n');
  };

  const downloadCsv = (csvContent: string) => {
    const blob = new Blob([`\uFEFF${csvContent}`], {
      type: 'text/csv;charset=utf-8;',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `base-zeuss-importada-${moment().format('YYYY-MM-DD')}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const getAllClientsToExport = async () => {
    const pageSize = 100;
    const firstResponse = await apiExportClients.callApi({
      page: 1,
      size: pageSize,
      status: getStatusFilter(),
    });

    if (!firstResponse.success) {
      throw new Error(firstResponse.mensagem || 'Erro ao buscar clientes.');
    }

    const firstPageClients = getClientsFromResponse(firstResponse.data);
    const totalPages = firstResponse.data?.totalPages || 1;
    const allClients = [...firstPageClients];

    for (let page = 2; page <= totalPages; page += 1) {
      const response = await apiExportClients.callApi({
        page,
        size: pageSize,
        status: getStatusFilter(),
      });

      if (!response.success) {
        throw new Error(response.mensagem || 'Erro ao buscar clientes.');
      }

      allClients.push(...getClientsFromResponse(response.data));
    }

    return allClients;
  };

  const handleDownloadImportedBase = async () => {
    setExporting(true);

    try {
      const clientsToExport = await getAllClientsToExport();

      if (!clientsToExport.length) {
        showFeedback('Nenhum cliente encontrado para exportar.');
        return;
      }

      downloadCsv(buildZeussCsv(clientsToExport));
    } catch (err) {
      showFeedback(err?.message || 'Erro ao exportar base importada.');
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    fetchClients();
  }, [form.status]);

  const handleDropFile = (file: File | null) => {
    if (!file) {
      setPendingFile(null);
      return;
    }

    const isXlsx = file.name.toLowerCase().endsWith('.xlsx');
    if (!isXlsx) {
      setPendingFile(null);
      showFeedback('Selecione um arquivo com extensão .xlsx.');
      return;
    }

    setPendingFile(file);
  };

  const { openModal } = useModal();

  const handleImport = async () => {
    if (!pendingFile) {
      showFeedback('Selecione um arquivo .xlsx para importar.');
      return;
    }

    setImporting(true);

    const token = localStorage.getItem(Constants.TOKEN_KEY);
    const tokenBearer = localStorage.getItem(Constants.TOKEN_BEARER);
    const formData = new FormData();
    formData.append('file', pendingFile);
    formData.append('fileExtension', 'xlsx');

    const config = {
      headers: {
        ...(token ? { apikey: `token ${token}` } : {}),
        ...(tokenBearer ? { Authorization: `Bearer ${tokenBearer}` } : {}),
        'Content-Type': 'multipart/form-data',
      },
    };

    try {
      const response = await Axios.post<{
        mensagem?: string;
        clientesJaExistentes: string[];
        clientesInseridos: number;
      }>(Endpoints.files.postImportZeussXslx, formData, config);

      const isSuccess = response.status >= 200 && response.status < 300;
      const message = response.data?.mensagem;

      if (!isSuccess) {
        showFeedback(message || 'Erro ao importar arquivo.');
        return;
      }

      setImportedFileName(pendingFile.name);
      setPendingFile(null);
      await fetchClients();
      const data = response.data;
      const jaExistentes = data?.clientesJaExistentes;
      const inseridos = data?.clientesInseridos;

      openModal({
        title: 'Atenção!',
        children: (
          <div>
            <p style={{ fontWeight: 600, fontSize: 18 }}>
              Foram importado(s) {inseridos} cliente(s)
            </p>

            {jaExistentes?.length > 0 && (
              <>
                <span style={{ fontWeight: 500, fontSize: 18, marginTop: 8 }}>
                  Os seguintes CPF's já existem na base de clientes e não foram
                  importados:
                </span>
                <p
                  style={{
                    fontWeight: 500,
                    fontSize: 16,
                    marginTop: 4,
                    fontStyle: 'italic',
                  }}
                >
                  {jaExistentes?.map((v) => cpfMask(v).masked)?.join(', ')}
                </p>
              </>
            )}
          </div>
        ),
      });
    } catch (err) {
      showFeedback(
        err?.response?.data?.mensagem || 'Erro ao importar arquivo.',
      );
    } finally {
      setImporting(false);
    }
  };

  const handleCancel = () => {
    setPendingFile(null);
  };

  const handleNewSpreadsheet = () => {
    setPendingFile(null);
    setImportedFileName(null);
    setFeedback({ open: false, message: '' });
  };

  const getClientId = (client: IZeussClient) => client.idCliente || client.id;

  const handleDeleteClient = (client: IZeussClient) => {
    const clientId = getClientId(client);
    if (!clientId) {
      showFeedback('Cliente sem identificador para exclusao.');
      return;
    }

    setDeleteClientId(clientId);
    setDialogDelete({
      open: true,
      message: `Deseja excluir o cliente ${client?.nome} ?`,
      clientId,
    });
  };

  const handleConfirmDelete = async () => {
    if (!dialogDelete.clientId) {
      showFeedback('Cliente sem identificador para exclusao.');
      setDialogDelete((prev) => ({ ...prev, open: false }));
      return;
    }

    try {
      const response = await apiDeleteClient.callApi({
        PARAM_ID_CLIENTE: dialogDelete.clientId,
      });

      if (!response.success) {
        showFeedback(response.mensagem || 'Erro ao excluir cliente.');
        return;
      }

      showFeedback(response.mensagem || 'Cliente excluido com sucesso.');
      await fetchClients();
    } catch {
      showFeedback('Erro ao excluir cliente.');
    } finally {
      setDialogDelete((prev) => ({ ...prev, open: false }));
      setDeleteClientId(null);
    }
  };

  const loading =
    apiGetActiveClients.loading ||
    importing ||
    apiDeleteClient.loading ||
    exporting;

  const currencyFormatter = new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });

  const tableRows = useMemo(() => {
    return clients.map((client) => ({
      id: getClientId(client),
      nome: client.nome || '-',
      cpf: client.cpf || '-',
      telefone:
        (client.telefone as string) || (client.celular as string) || '-',
      email: client.email || '-',
      limite:
        typeof client.limite === 'number'
          ? currencyFormatter.format(client.limite)
          : '-',
      dataImportacao: client.dataImportacao
        ? moment(client.dataImportacao).format('DD/MM/YYYY')
        : '-',
      original: client,
      status: client?.status,
    }));
  }, [clients]);

  return (
    <>
      <Dialog
        open={feedback.open}
        message={feedback.message}
        positiveAction={() => setFeedback({ open: false, message: '' })}
        positiveLabel="Fechar"
      />
      <Dialog
        open={dialogDelete.open}
        message={dialogDelete.message}
        positiveAction={handleConfirmDelete}
        positiveLabel="Confirmar"
        negativeLabel="Cancelar"
        negativeAction={() =>
          setDialogDelete((prev) => ({
            ...prev,
            open: false,
            clientId: null,
          }))
        }
        Loading={apiDeleteClient.loading}
      />

      <Loading status={loading} />

      <St.BoxCard>
        <ContainerFlex>
          <ContainerFlex padding={16}>
            {importedFileName ? (
              <ContainerFlex
                row
                space
                horizontal
                center
                style={{
                  backgroundColor: '#DDF3E9',
                  padding: 16,
                  borderRadius: 8,
                  marginBottom: 8,
                }}
              >
                <span style={{ color: '#25463A', fontWeight: 500 }}>
                  {`Planilha ${importedFileName} importada!`}
                </span>
                <Button fitContent onClick={handleNewSpreadsheet}>
                  Nova planilha
                </Button>
              </ContainerFlex>
            ) : (
              <>
                <LocalDropzone
                  accept=".xlsx"
                  containerStyle={{
                    width: '100%',
                    marginLeft: 0,
                    border: '3px dashed #C0C0C0',
                  }}
                  label="Arraste ou selecione um arquivo (.xlsx)"
                  onFileUploaded={handleDropFile}
                  dropZoneOptions={{
                    multiple: false,
                    maxFiles: 1,
                    accept: {
                      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [
                        '.xlsx',
                      ],
                    },
                  }}
                />

                {pendingFile && (
                  <ContainerFlex row space padding={16}>
                    <div style={{ flex: 1, overflow: 'hidden' }}>
                      <strong>Arquivo:</strong>{' '}
                      <span style={{ wordBreak: 'break-all' }}>
                        {pendingFile.name}
                      </span>
                    </div>

                    <Button
                      fitContent
                      onClick={handleImport}
                      loading={importing}
                    >
                      Importar
                    </Button>
                    <Button fitContent marginLeft={16} onClick={handleCancel}>
                      Cancelar
                    </Button>
                  </ContainerFlex>
                )}
              </>
            )}

            <ContainerFlex
              marginTop={16}
              row
              space
              horizontal
              center
              style={{
                backgroundColor: '#F1F4F6',
                padding: 16,
                borderRadius: 8,
              }}
            >
              <span style={{ fontSize: 18, fontWeight: 600 }}>
                Planilha de exemplo
              </span>
              <Button fitContent onClick={downloadZeussDemoXlsx}>
                Download
              </Button>
            </ContainerFlex>

            <ContainerFlex marginTop={16}>
              <ContainerFlex row center space marginTop={8} marginBottom={8}>
                <ContainerFlex>
                  <span
                    style={{ fontSize: 18, fontWeight: 600, marginBottom: 12 }}
                  >
                    Total de Clientes: {apiGetActiveClients?.data?.totalSize}
                  </span>
                </ContainerFlex>
                <ContainerFlex>
                  <Select
                    label="Status"
                    value={form.status}
                    options={[
                      { id: 'TODOS', value: 'Todos' },
                      { id: 'ATIVO', value: 'Ativo' },
                      { id: 'INATIVO', value: 'Inativo' },
                      { id: 'EXCLUIDO', value: 'Excluído' },
                    ]}
                    onChange={(e) => {
                      setForm({ ...form, status: e });
                    }}
                  />
                </ContainerFlex>
              </ContainerFlex>

              <St.TableWrapper>
                <St.ZeussTable>
                  <thead style={{ position: 'sticky', top: 0, zIndex: 2 }}>
                    <tr>
                      <St.ZeussHeaderCell first>
                        Nome Completo
                      </St.ZeussHeaderCell>
                      <St.ZeussHeaderCell>CPF</St.ZeussHeaderCell>
                      <St.ZeussHeaderCell>Telefone</St.ZeussHeaderCell>
                      <St.ZeussHeaderCell>E-mail</St.ZeussHeaderCell>
                      <St.ZeussHeaderCell>Limite Concedido</St.ZeussHeaderCell>
                      <St.ZeussHeaderCell>
                        Data de Importação
                      </St.ZeussHeaderCell>
                      <St.ZeussHeaderCell>Status</St.ZeussHeaderCell>
                      <St.ZeussHeaderCell last>Ação</St.ZeussHeaderCell>
                    </tr>
                  </thead>
                  <tbody>
                    {tableRows.length === 0 ? (
                      <tr>
                        <St.ZeussEmptyCell colSpan={8}>
                          Nenhum cliente encontrado.
                        </St.ZeussEmptyCell>
                      </tr>
                    ) : (
                      tableRows.map((row) => (
                        <St.ZeussRow key={`${row.id}`}>
                          <St.ZeussCell first>{row.nome}</St.ZeussCell>
                          <St.ZeussCell>{cpfMask(row.cpf).masked}</St.ZeussCell>
                          <St.ZeussCell>
                            {phoneMask(row.telefone).masked}
                          </St.ZeussCell>
                          <St.ZeussCell>{row.email}</St.ZeussCell>
                          <St.ZeussCell>{row.limite}</St.ZeussCell>
                          <St.ZeussCell>{row.dataImportacao}</St.ZeussCell>
                          <St.ZeussCell>{row.status}</St.ZeussCell>
                          <St.ZeussCell last>
                            {row.status !== 'EXCLUIDO' ? (
                              <div
                                className="delete-button"
                                onClick={() => {
                                  if (!apiDeleteClient.loading) {
                                    handleDeleteClient(row.original);
                                  }
                                }}
                              >
                                <IconTrash />
                              </div>
                            ) : null}
                          </St.ZeussCell>
                        </St.ZeussRow>
                      ))
                    )}
                  </tbody>
                </St.ZeussTable>
              </St.TableWrapper>
              <ContainerFlex
                row
                horizontal
                center
                marginTop={20}
                style={{ position: 'relative', width: '100%' }}
              >
                <Pagination
                  totalPages={apiGetActiveClients?.data?.totalPages ?? 1}
                  onCurrentPageChange={fetchClients}
                />
                <div style={{ position: 'absolute', right: 0 }}>
                  <Button
                    fitContent
                    loading={exporting}
                    disabled={exporting || !apiGetActiveClients?.data?.totalSize}
                    onClick={handleDownloadImportedBase}
                  >
                    Download da base importada
                  </Button>
                </div>
              </ContainerFlex>
            </ContainerFlex>
          </ContainerFlex>
        </ContainerFlex>
      </St.BoxCard>
    </>
  );
};

export default ZeussFileUpload;
function IconTrash() {
  return (
    <svg
      width="19"
      height="19"
      viewBox="0 0 19 19"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M15.0418 4.75V15.8333C15.0418 16.2533 14.875 16.656 14.5781 16.9529C14.2811 17.2499 13.8784 17.4167 13.4585 17.4167H5.54183C5.1219 17.4167 4.71918 17.2499 4.42224 16.9529C4.12531 16.656 3.9585 16.2533 3.9585 15.8333V4.75"
        stroke="#E4003A"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M2.375 4.75H16.625"
        stroke="#E4003A"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M6.3335 4.75004V3.16671C6.3335 2.74678 6.50031 2.34405 6.79724 2.04712C7.09418 1.75019 7.4969 1.58337 7.91683 1.58337H11.0835C11.5034 1.58337 11.9061 1.75019 12.2031 2.04712C12.5 2.34405 12.6668 2.74678 12.6668 3.16671V4.75004"
        stroke="#E4003A"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </svg>
  );
}
